import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { IndianRupee, Upload, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const vastuOnline = [
  { id: 'basic_report', name: 'Basic Vastu Report', nameHi: 'बेसिक वास्तु रिपोर्ट', price: 7000, desc: 'Upload floor plan, get PDF report in 48 hours', descHi: 'फ्लोर प्लान अपलोड करें, 48 घंटे में PDF रिपोर्ट प्राप्त करें' },
  { id: 'home_video', name: 'Home Vastu Video Call', nameHi: 'होम वास्तु वीडियो कॉल', price: 9000, desc: 'Comprehensive home vastu analysis via video call', descHi: 'वीडियो कॉल के माध्यम से व्यापक होम वास्तु विश्लेषण' },
  { id: 'office_video', name: 'Office/Shop Vastu Video Call', nameHi: 'ऑफिस/शॉप वास्तु वीडियो कॉल', price: 11000, desc: 'Complete office/shop vastu consultation', descHi: 'पूर्ण ऑफिस/शॉप वास्तु परामर्श' },
  { id: 'factory_video', name: 'Factory/Industrial Vastu Video Call', nameHi: 'फैक्ट्री/इंडस्ट्रियल वास्तु', price: 15000, desc: 'Large-scale industrial vastu guidance', descHi: 'बड़े पैमाने पर औद्योगिक वास्तु मार्गदर्शन' }
];

const VastuServicesPage = () => {
  const { i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isHindi = i18n.language === 'hi';
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', city: '', state: '', property_type: 'Home', property_size: '', preferred_visit_date: '', issues: '', description: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) { navigate('/login'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(enquiryForm);
      let url = `${API}/enquiries/site-visit-with-file?${params.toString()}`;
      
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(url, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/enquiries/site-visit`, enquiryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error submitting enquiry');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 lg:px-24 pb-16">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-serif text-[#F5F2EB] text-center mb-4" data-testid="vastu-heading">
          {isHindi ? 'वास्तु सेवाएं' : 'Vastu Services'}
        </h1>
        <p className="text-[#A3A3A3] text-center mb-12 max-w-2xl mx-auto">
          {isHindi ? 'अपने स्थान के लिए पेशेवर वास्तु परामर्श' : 'Professional Vastu consultations for your spaces'}
        </p>

        <h2 className="text-2xl font-serif text-[#C9A84C] mb-6">{isHindi ? 'ऑनलाइन सेवाएं' : 'Online Services'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {vastuOnline.map((service) => (
            <motion.div key={service.id} whileHover={{ y: -5 }}
              className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-6 transition-all hover:shadow-[0_0_20px_rgba(201,168,76,0.1)]">
              <h3 className="text-xl font-serif text-[#F5F2EB] mb-2">{isHindi ? service.nameHi : service.name}</h3>
              <p className="text-[#A3A3A3] text-sm mb-4">{isHindi ? service.descHi : service.desc}</p>
              <div className="flex items-center gap-2 text-[#C9A84C] text-2xl font-bold mb-4">
                <IndianRupee className="w-6 h-6" />
                <span>{service.price.toLocaleString()}</span>
              </div>
              <Link to="/booking" state={{ packageType: service.id, serviceType: 'vastu' }}
                className="block w-full bg-[#C9A84C] text-[#0A0A0A] text-center font-medium px-6 py-3 hover:bg-[#E5C870] transition-colors uppercase tracking-wider text-sm"
                data-testid={`vastu-book-${service.id}`}>
                {isHindi ? 'बुक करें' : 'Book Now'}
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="border-t border-[#C9A84C]/15 pt-16">
          <h2 className="text-2xl font-serif text-[#C9A84C] mb-6">{isHindi ? 'साइट विजिट' : 'Site Visit'}</h2>
          <p className="text-[#A3A3A3] mb-8">
            {isHindi ? 'कस्टम कोट प्राप्त करें - कोई अग्रिम भुगतान नहीं। हम आपकी आवश्यकताओं की समीक्षा करेंगे।' : 'Get a custom quote - no upfront payment required. We will review your requirements and send a personalized quote.'}
          </p>

          {submitted ? (
            <div className="bg-[#C9A84C]/10 border border-[#C9A84C]/30 p-8 text-center">
              <Send className="w-12 h-12 text-[#C9A84C] mx-auto mb-4" />
              <h3 className="text-2xl font-serif text-[#F5F2EB] mb-2">{isHindi ? 'पूछताछ जमा!' : 'Enquiry Submitted!'}</h3>
              <p className="text-[#A3A3A3]">{isHindi ? 'हम जल्द ही आपसे संपर्क करेंगे।' : 'We will contact you shortly with a custom quote.'}</p>
            </div>
          ) : (
            <div>
              {!showEnquiry ? (
                <button onClick={() => setShowEnquiry(true)}
                  className="bg-transparent border border-[#C9A84C]/50 text-[#C9A84C] font-medium px-8 py-3 hover:bg-[#C9A84C]/10 transition-colors uppercase tracking-wider text-sm"
                  data-testid="site-visit-enquiry-btn">
                  {isHindi ? 'साइट विजिट पूछताछ फॉर्म' : 'Fill Site Visit Enquiry Form'}
                </button>
              ) : (
                <form onSubmit={handleEnquirySubmit} className="bg-[#1A1A1A] border border-[#C9A84C]/15 p-8 max-w-2xl" data-testid="site-visit-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'नाम' : 'Name'}</label>
                      <input value={enquiryForm.name} onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                        className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                    <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'फोन' : 'Phone'}</label>
                      <input value={enquiryForm.phone} onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })}
                        className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                    <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'शहर' : 'City'}</label>
                      <input value={enquiryForm.city} onChange={(e) => setEnquiryForm({ ...enquiryForm, city: e.target.value })}
                        className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                    <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'राज्य' : 'State'}</label>
                      <input value={enquiryForm.state} onChange={(e) => setEnquiryForm({ ...enquiryForm, state: e.target.value })}
                        className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                    <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'संपत्ति प्रकार' : 'Property Type'}</label>
                      <select value={enquiryForm.property_type} onChange={(e) => setEnquiryForm({ ...enquiryForm, property_type: e.target.value })}
                        className="w-full bg-[#1A1A1A] border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none">
                        <option value="Home">Home</option><option value="Office">Office</option><option value="Factory">Factory</option><option value="Plot">Plot</option>
                      </select></div>
                    <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'क्षेत्रफल (वर्ग फीट)' : 'Property Size (sq ft)'}</label>
                      <input value={enquiryForm.property_size} onChange={(e) => setEnquiryForm({ ...enquiryForm, property_size: e.target.value })}
                        className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                    <div><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'पसंदीदा विजिट तारीख' : 'Preferred Visit Date'}</label>
                      <input type="date" value={enquiryForm.preferred_visit_date} onChange={(e) => setEnquiryForm({ ...enquiryForm, preferred_visit_date: e.target.value })}
                        className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
                  </div>
                  <div className="mt-4"><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'आप किन समस्याओं का सामना कर रहे हैं?' : 'What issues are you facing?'}</label>
                    <textarea value={enquiryForm.issues} onChange={(e) => setEnquiryForm({ ...enquiryForm, issues: e.target.value })} rows={3}
                      className="w-full bg-transparent border border-[#C9A84C]/30 p-2 mt-1 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
                  <div className="mt-4"><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'फ्लोर प्लान अपलोड (PDF/Image)' : 'Upload Floor Plan (PDF/Image)'}</label>
                    <div className="mt-2 border border-dashed border-[#C9A84C]/30 p-4 text-center">
                      <Upload className="w-8 h-8 text-[#C9A84C] mx-auto mb-2" />
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])}
                        className="text-[#A3A3A3] text-sm" data-testid="floor-plan-upload" />
                      {file && <p className="text-[#C9A84C] text-sm mt-2">{file.name}</p>}
                    </div>
                  </div>
                  <div className="mt-4"><label className="text-xs text-[#A3A3A3] uppercase">{isHindi ? 'विवरण' : 'Description'}</label>
                    <textarea value={enquiryForm.description} onChange={(e) => setEnquiryForm({ ...enquiryForm, description: e.target.value })} rows={2}
                      className="w-full bg-transparent border border-[#C9A84C]/30 p-2 mt-1 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none"
                      placeholder={isHindi ? 'आप क्या चाहते हैं...' : 'What are you looking for...'} /></div>
                  <button type="submit" disabled={submitting}
                    className="mt-6 w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] disabled:opacity-40 uppercase tracking-wider text-sm"
                    data-testid="submit-enquiry-btn">
                    {submitting ? 'Submitting...' : (isHindi ? 'पूछताछ भेजें' : 'Submit Enquiry')}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VastuServicesPage;
